import React, { useEffect, useState, memo } from "react";
import { ResponsiveBar } from "@nivo/bar";
import { useUserContext } from "../../context";
import { colors } from "../../devdata/constants/themes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate } from "@fortawesome/free-solid-svg-icons";
import { Subhai } from "..";

// Custom bar component wrapped with React.memo
const CustomBarComponent = memo(
  ({ bar, onSectorClick, animate, selectedSector }) => {
    const [showTooltip, setShowTooltip] = useState(false);

    if (!bar || !bar.data || !bar.data.data) {
      return null; // Return null if bar data is not available
    }

    const isPositive = Number(bar.data.data.value) > 0;
    const radius = 5;
    let gradientId, gradientStops, d;

    if (isPositive) {
      gradientId = `gradient-${bar.data.name}-pos`;
      gradientStops = (
        <>
          <stop offset="0%" stopColor="#01e47c" /> {/* Normal green */}
          <stop offset="100%" stopColor="#005930" /> {/* Dark green */}
        </>
      );
      d = `M0,${bar.height} 
         L0,${radius} 
         Q0,0 ${radius},0 
         L${bar.width - radius},0 
         Q${bar.width},0 ${bar.width},${radius} 
         L${bar.width},${bar.height} Z`;
    } else {
      gradientId = `gradient-${bar.data.name}-neg`;
      gradientStops = (
        <>
          <stop offset="  0%" stopColor="#7f3c3c" /> {/* Dark red */}
          <stop offset="100%" stopColor="#ff6061" /> {/* Normal red */}
        </>
      );
      d = `M0,0 
         L0,${bar.height - radius} 
         Q0,${bar.height} ${radius},${bar.height} 
         L${bar.width - radius},${bar.height} 
         Q${bar.width},${bar.height} ${bar.width},${bar.height - radius} 
         L${bar.width},0 Z`;
    }

    // For positive bars, the baseline is at the bottom (y = bar.height); for negative, at the top.
    const transformOrigin = isPositive ? `0 ${bar.height}px` : `0 0`;

    return (
      <g
        transform={`translate(${bar.x}, ${bar.y})`}
        onClick={(e) => {
          e.stopPropagation();
          onSectorClick(bar.data.data.name);
        }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        style={{ cursor: "pointer" }}
      >
        <g
          style={{
            zIndex: 1,
            transformOrigin: transformOrigin,
            transform: `scaleY(${animate ? 1 : 0})`,
            transition: "transform 0.5s ease-out",
          }}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              {gradientStops}
            </linearGradient>
          </defs>

          <path
            d={d}
            fill={`url(#${gradientId})`}
            stroke={
              bar.data.data.name === selectedSector
                ? "#ffffff"
                : isPositive
                  ? "#01e47c"
                  : "#ff6061"
            }
            strokeWidth={bar.data.data.name === selectedSector ? 2 : 1}
            opacity={bar.data.data.name === selectedSector ? 1 : 0.8}
          />
        </g>
        {showTooltip && (
          <foreignObject
            x={-50}
            y={0}
            width="130"
            height="35"
            style={{ zIndex: 1000, pointerEvents: "none" }}
          >
            <div
              className="bg-darkMode-layer2 text-white px-3 py-2 rounded-lg text-sm shadow-lg"
              style={{
                position: "absolute",
                whiteSpace: "nowrap",
                textAlign: "center",
                zIndex: 1000,
              }}
            >
              {`${bar.data.data.name}: ${bar.data.data.value}%`}
            </div>
          </foreignObject>
        )}
      </g>
    );
  }
);

const BarChart = ({ onSectorClick }) => {
  const [data, setData] = useState([]);
  const [updatedTime, setUpdatedTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [selectedSector, setSelectedSector] = useState("All Sector");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { userdetails } = useUserContext();

  useEffect(() => {
    // Load data from sessionStorage
    const storedData = sessionStorage.getItem("index");
    if (storedData && storedData !== "undefined") {
      const parsedData = JSON.parse(storedData) || [];
      setData(parsedData);
      setUpdatedTime(
        new Date().toLocaleString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
      );
    }

    const interval = setInterval(() => {
      const storedData = sessionStorage.getItem("index");
      if (storedData && storedData !== "undefined") {
        const parsedData = JSON.parse(storedData) || [];
        setData(parsedData);
        setUpdatedTime(
          new Date().toLocaleString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })
        );
      }
    }, 1000);
    // Trigger animation on initial load
    const timer = setTimeout(() => {
      setAnimate(true);
    }, 50);
    // Listen for resize to update isMobile state
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", handleResize);
      clearInterval(interval);
    };
  }, []);

  const sortedData = data.sort((a, b) => b.per_chg - a.per_chg);
  const chartData = sortedData.map((item) => ({
    name: item.name,
    value: item.per_chg,
  }));

  const refreshData = () => {
    setLoading(true);
    setTimeout(() => setLoading(false), 1000);
  };

  const handleSectorClick = (sector) => {
    setSelectedSector(sector);
    onSectorClick(sector);
  };

  const handleBackgroundClick = () => {
    setSelectedSector("All Sectors");
    onSectorClick("All Sectors");
  };

  // Mobile-specific bar component
  const MobileBarComponent = (props) => (
    <CustomBarComponent
      {...props}
      onSectorClick={handleSectorClick}
      animate={animate}
      selectedSector={selectedSector}
    />
  );

  // Desktop-specific bar component
  const DesktopBarComponent = (props) => (
    <CustomBarComponent
      {...props}
      onSectorClick={handleSectorClick}
      animate={animate}
      selectedSector={selectedSector}
    />
  );

  return (
    <div
      className="px-4 py-4 bg-darkMode-layer1 rounded-lg shadow-lg flex flex-col items-center"
      onClick={handleBackgroundClick}
    >
      <div
        className="flex flex-row justify-between items-center w-full"
        onClick={(e) => e.stopPropagation()} // Prevent triggering background click when clicking header
      >
        <div>
          <h2 className="text-xl font-semibold text-white">
            Momentum Analysis-Sector
          </h2>
          <div className="flex flex-row w-full justify-start">
            <div className="bg-darkMode-layer2 px-2 py-1 rounded-full text-xs text-darkMode-subText mt-2 mb-4">
              {updatedTime && `Last Updated: ${updatedTime}`}
            </div>
          </div>
        </div>
        <button
          onClick={refreshData}
          className="bg-darkMode-layer2 text-darkMode-text flex items-center text-sm px-4 py-4 rounded-xl shadow-md transition-all duration-300 ease-in-out hover:ring-2 hover:ring-darkMode-green cursor-pointer"
        >
          <FontAwesomeIcon
            icon={faArrowsRotate}
            size="lg"
            className={loading ? "animate-spin" : ""}
          />
        </button>
      </div>

      {isMobile ? (
        <div className="w-full h-64 relative">
          <Subhai />

          <ResponsiveBar
            data={chartData}
            keys={["value"]}
            indexBy="name"
            margin={{ top: 20, right: 10, bottom: 80, left: 40 }}
            padding={0.2}
            colors={({ id, data }) =>
              data && data.value > 0 ? "#01e47c" : "#ff6061"
            }
            borderColor={{ from: "color", modifiers: [["darker", 1.6]] }}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 90,
            }}
            axisLeft={{
              legend: "% Change",
              legendPosition: "middle",
              legendOffset: -30,
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
            }}
            labelSkipWidth={12}
            labelSkipHeight={12}
            labelTextColor={{ from: "color", modifiers: [["darker", 1.6]] }}
            animate={animate}
            motionStiffness={90}
            motionDamping={15}
            theme={{
              axis: {
                ticks: {
                  text: { fill: colors.darkMode.text, fontSize: 10 },
                },
                legend: {
                  text: { fill: colors.darkMode.layer3, fontSize: 10 },
                },
              },
              grid: {
                line: {
                  stroke: colors.darkMode.layer3,
                },
              },
            }}
            barComponent={MobileBarComponent}
          />
        </div>
      ) : (
        <div className="w-full h-96 relative">
          <Subhai />

          <ResponsiveBar
            data={chartData}
            keys={["value"]}
            indexBy="name"
            margin={{ top: 20, right: 20, bottom: 70, left: 50 }}
            padding={0.3}
            colors={({ id, data }) =>
              data && data.value > 0 ? "#01e47c" : "#ff6061"
            }
            borderColor={{ from: "color", modifiers: [["darker", 1.6]] }}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 45,
            }}
            axisLeft={{
              legend: "% Change",
              legendPosition: "middle",
              legendOffset: -40,
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
            }}
            labelSkipWidth={12}
            labelSkipHeight={12}
            labelTextColor={{ from: "color", modifiers: [["darker", 1.6]] }}
            animate={animate}
            motionStiffness={90}
            motionDamping={15}
            theme={{
              axis: {
                ticks: {
                  text: { fill: colors.darkMode.text },
                },
                legend: {
                  text: { fill: colors.darkMode.layer3 },
                },
              },
              grid: {
                line: {
                  stroke: colors.darkMode.layer3,
                },
              },
            }}
            barComponent={DesktopBarComponent}
          />
        </div>
      )}
    </div>
  );
};

export default BarChart;
